/**
 * SSE token utilities.
 *
 * Fetches a short-lived JWT from the server-side resource route
 * and builds the full SSE URL pointing at the real API.
 */

import type { SseTokenDto } from '~/lib/sse-client';

const SSE_STREAM_PATH = '/v1/gamification/xp-gains/stream';

/** Extends the API's SseTokenDto with the baseUrl injected by our resource route. */
type SseTokenResponse = SseTokenDto & { baseUrl: string };

/**
 * Fetch an SSE token and API base URL from the resource route.
 * The resource route proxies `POST /v1/auth/sse-token` with the
 * user's session cookie.
 */
async function fetchSseToken(): Promise<SseTokenResponse> {
  const res = await fetch('/auth/sse-token', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`SSE token request failed: ${res.status}`);
  }
  return res.json() as Promise<SseTokenResponse>;
}

/**
 * Build the full SSE URL with the token as a query parameter.
 * `sinceId` can be passed to skip events already seen.
 */
export async function buildSseUrlWithToken(sinceId?: string): Promise<string> {
  const { token, baseUrl } = await fetchSseToken();
  const params = new URLSearchParams({ token });
  if (sinceId) {
    params.set('sinceId', sinceId);
  }
  return `${baseUrl}${SSE_STREAM_PATH}?${params}`;
}
